import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  Badge
} from '@mui/material';
import {
  ElectricBolt as ElectricIcon,
  Water as WaterIcon,
  LocalFireDepartment as GasIcon,
  Speed as MeterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CameraAlt as CameraIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  History as HistoryIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`meter-tabpanel-${index}`}
      aria-labelledby={`meter-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const MeterReadingPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [readings, setReadings] = useState<any[]>([]);
  const [sitesNeedingReading, setSitesNeedingReading] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [meterConfigs, setMeterConfigs] = useState<any[]>([]);
  
  // UI states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMeterType, setSelectedMeterType] = useState('electric');
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(30, 'day'),
    endDate: dayjs()
  });
  
  // Dialog states
  const [readingDialog, setReadingDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  
  // Form states
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [readingForm, setReadingForm] = useState({
    value: '',
    notes: '',
    meterType: 'electric',
    readingDate: dayjs()
  });
  const [bulkReadings, setBulkReadings] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, [selectedMeterType, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchReadings(),
        fetchSitesNeedingReading(),
        fetchStatistics(),
        fetchMeterConfigs()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReadings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/meters/readings`,
        {
          params: {
            meterType: selectedMeterType,
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString(),
            page: page + 1,
            limit: rowsPerPage
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setReadings(response.data.readings);
    } catch (error) {
      console.error('Error fetching readings:', error);
    }
  };

  const fetchSitesNeedingReading = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/meters/sites-needing-reading`,
        {
          params: {
            meterType: selectedMeterType,
            daysSinceLastReading: 30
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSitesNeedingReading(response.data);
    } catch (error) {
      console.error('Error fetching sites needing reading:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/meters/statistics`,
        {
          params: {
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString()
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchMeterConfigs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/meters/configs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMeterConfigs(response.data);
    } catch (error) {
      console.error('Error fetching meter configs:', error);
    }
  };

  const handleSubmitReading = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/meters/readings`,
        {
          siteId: selectedSite._id,
          meterType: readingForm.meterType,
          value: parseFloat(readingForm.value),
          notes: readingForm.notes,
          readingDate: readingForm.readingDate.toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReadingDialog(false);
      setReadingForm({
        value: '',
        notes: '',
        meterType: 'electric',
        readingDate: dayjs()
      });
      fetchData();
      alert('Reading submitted successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error submitting reading');
    }
  };

  const handleSubmitBulkReadings = async () => {
    try {
      const readingsData = bulkReadings.map(r => ({
        siteId: r.siteId,
        meterType: selectedMeterType,
        value: parseFloat(r.value),
        readingDate: dayjs().toISOString()
      }));
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/meters/readings/bulk`,
        { readings: readingsData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBulkDialog(false);
      setBulkReadings([]);
      fetchData();
      
      alert(`Submitted ${response.data.successful.length} readings successfully. ${response.data.failed.length} failed.`);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error submitting bulk readings');
    }
  };

  const handleChargeReading = async (readingId: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/meters/readings/${readingId}/charge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchData();
      alert('Reading charged successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error charging reading');
    }
  };

  const getMeterIcon = (type: string) => {
    switch (type) {
      case 'electric':
        return <ElectricIcon />;
      case 'water':
        return <WaterIcon />;
      case 'gas':
      case 'propane':
        return <GasIcon />;
      default:
        return <MeterIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'billed':
        return 'info';
      case 'disputed':
        return 'warning';
      case 'adjusted':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode, trend?: number) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" gutterBottom>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {trend > 0 ? (
              <TrendingUpIcon color="success" fontSize="small" />
            ) : (
              <TrendingDownIcon color="error" fontSize="small" />
            )}
            <Typography
              variant="body2"
              color={trend > 0 ? 'success.main' : 'error.main'}
              sx={{ ml: 0.5 }}
            >
              {Math.abs(trend)}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Meter Reading Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track and manage utility meter readings
        </Typography>
      </Box>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Total Readings',
              statistics.totalReadings,
              'This period',
              <MeterIcon color="primary" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Total Usage',
              statistics.totalUsage.toLocaleString(),
              'Units consumed',
              getMeterIcon(selectedMeterType)
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Revenue',
              `$${statistics.totalRevenue.toFixed(2)}`,
              'From meter charges',
              <MoneyIcon color="success" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Unbilled',
              statistics.uncharged,
              'Readings to charge',
              <WarningIcon color="warning" />
            )}
          </Grid>
        </Grid>
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setReadingDialog(true)}
        >
          New Reading
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setBulkDialog(true)}
        >
          Bulk Entry
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<ReportIcon />}
        >
          Generate Report
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setConfigDialog(true)}
        >
          Configure Meters
        </Button>
      </Stack>

      {/* Meter Type Selector */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedMeterType} onChange={(e, v) => setSelectedMeterType(v)}>
          <Tab 
            label="Electric" 
            value="electric" 
            icon={<ElectricIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Water" 
            value="water" 
            icon={<WaterIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Gas" 
            value="gas" 
            icon={<GasIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Propane" 
            value="propane" 
            icon={<GasIcon />} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Recent Readings" />
          <Tab label="Sites Needing Reading" />
          <Tab label="Usage History" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Recent Readings */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Reading</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Charges</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readings.map((reading) => (
                  <TableRow key={reading._id}>
                    <TableCell>
                      {dayjs(reading.readingDate).format('MM/DD/YYYY')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Site {reading.site?.siteNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{reading.reading.value}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {reading.reading.usage}
                        {reading.flags.highUsage && (
                          <Tooltip title="High usage detected">
                            <WarningIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      ${reading.billing.totalAmount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reading.status}
                        size="small"
                        color={getStatusColor(reading.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {!reading.billing.charged && reading.status === 'confirmed' && (
                          <Tooltip title="Charge to reservation">
                            <IconButton
                              size="small"
                              onClick={() => handleChargeReading(reading._id)}
                            >
                              <MoneyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View history">
                          <IconButton size="small">
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={100} // This should come from API
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Sites Needing Reading */}
          {sitesNeedingReading.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                All sites are up to date!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {sitesNeedingReading.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.site._id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">
                          Site {item.site.siteNumber}
                        </Typography>
                        <Chip
                          label={`${Math.floor(item.daysSinceLastReading)} days ago`}
                          size="small"
                          color="warning"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Reading: {item.lastReading.reading.value}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        Date: {dayjs(item.lastReading.readingDate).format('MM/DD/YYYY')}
                      </Typography>
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setSelectedSite(item.site);
                          setReadingDialog(true);
                        }}
                      >
                        Enter Reading
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Usage History */}
          <Typography variant="body1" color="text.secondary">
            Usage history visualization coming soon...
          </Typography>
        </TabPanel>
      </Paper>

      {/* New Reading Dialog */}
      <Dialog open={readingDialog} onClose={() => setReadingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Meter Reading</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {selectedSite && (
              <Alert severity="info">
                Site {selectedSite.siteNumber} - {selectedSite.type}
              </Alert>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Meter Type</InputLabel>
              <Select
                value={readingForm.meterType}
                onChange={(e) => setReadingForm({ ...readingForm, meterType: e.target.value })}
              >
                <MenuItem value="electric">Electric</MenuItem>
                <MenuItem value="water">Water</MenuItem>
                <MenuItem value="gas">Gas</MenuItem>
                <MenuItem value="propane">Propane</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Reading Value"
              type="number"
              value={readingForm.value}
              onChange={(e) => setReadingForm({ ...readingForm, value: e.target.value })}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {readingForm.meterType === 'electric' ? 'kWh' : 
                     readingForm.meterType === 'water' ? 'gal' : 'units'}
                  </InputAdornment>
                )
              }}
            />
            
            <DatePicker
              label="Reading Date"
              value={readingForm.readingDate}
              onChange={(date) => date && setReadingForm({ ...readingForm, readingDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
            
            <TextField
              label="Notes (optional)"
              multiline
              rows={2}
              value={readingForm.notes}
              onChange={(e) => setReadingForm({ ...readingForm, notes: e.target.value })}
              fullWidth
            />
            
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              fullWidth
            >
              Add Photo
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReadingDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitReading} 
            variant="contained"
            disabled={!readingForm.value}
          >
            Submit Reading
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Entry Dialog */}
      <Dialog open={bulkDialog} onClose={() => setBulkDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Meter Entry - {selectedMeterType}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter readings for multiple sites at once. All readings will use today's date.
          </Alert>
          
          <List>
            {sitesNeedingReading.map((item) => (
              <React.Fragment key={item.site._id}>
                <ListItem>
                  <ListItemText
                    primary={`Site ${item.site.siteNumber}`}
                    secondary={`Last reading: ${item.lastReading.reading.value} (${Math.floor(item.daysSinceLastReading)} days ago)`}
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      placeholder="New reading"
                      size="small"
                      sx={{ width: 150 }}
                      onChange={(e) => {
                        const existing = bulkReadings.find(r => r.siteId === item.site._id);
                        if (existing) {
                          setBulkReadings(bulkReadings.map(r => 
                            r.siteId === item.site._id 
                              ? { ...r, value: e.target.value }
                              : r
                          ));
                        } else {
                          setBulkReadings([...bulkReadings, {
                            siteId: item.site._id,
                            value: e.target.value
                          }]);
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitBulkReadings} 
            variant="contained"
            disabled={bulkReadings.length === 0}
          >
            Submit {bulkReadings.length} Readings
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MeterReadingPage;